import { ObservabilityLayout } from '@/components/organisms/observability/ObservabilityLayout';

/**
 * Observability, as the Admin panel's fifth tab.
 *
 * A SHELL, not a rewrite. `ObservabilityLayout` is 812 lines of hand-rolled SVG charts with its own
 * period control, product filter, insight drawer and CSV export, and it worked as a page. This
 * adapts it to the panel's chrome and changes nothing about it — moving a module and redesigning it
 * are two changes, and doing them together would leave nobody able to tell which one broke it.
 *
 * WHAT THE SHELL ACTUALLY DOES. Two things the page used to provide and a tab does not:
 *
 *   - the SCROLL REGION. `ObservabilityLayout`'s root is `mx-auto max-w-[1600px] flex-col`, with no
 *     height and no overflow of its own — `ObservabilityPage` wrapped it in `flex-1 overflow-y-auto`.
 *     Dropped straight into a `TabsContent`, its content would grow the panel and hand the scroll to
 *     the document, which `qa:admin` fails the run for (§8.5: no horizontal or vertical scrolling of
 *     the document).
 *   - the one-line DESCRIPTION, matching the toolbar every other tab opens with. The page's
 *     `PageHeader` is deliberately not reused: the panel already has a page heading, and a second
 *     one inside a tabpanel reads as a nested page.
 *
 * LAZY BY CONSTRUCTION, which is the requirement this move came with. Radix `TabsContent` mounts no
 * children while inactive (`OrgAdminLayout` documents why `forceMount` is not used), so nothing here
 * exists — and therefore nothing here fetches — until the tab is selected. That is worth stating
 * because it is easy to break in a way that looks identical on screen: hoisting `useObservabilityEvents`
 * up to `OrgAdminLayout`, or adding `forceMount` for a scroll-position fix, would fire the
 * observability GraphQL query on every visit to the Admin panel. `qa/observability.mjs` asserts the
 * request count is zero until the click.
 *
 * Returning to the tab does not refetch either: `useObservabilityEvents` sets `staleTime: Infinity`
 * with `refetchOnMount: false`, so a remount reads the cache. That is the hook's own choice, made
 * before this move, and it is the right one for an append-only event log.
 *
 * NO AUTHORITY GATE, deliberately. Billing and Usage render a "needs admin authority" state for a
 * plain member because their endpoints 403 for one. Observability had no such rule as a page — it sat
 * ungated in the sidebar's Delivery group, visible to every member — so requiring authority here
 * would be inventing a permission, not honouring one. What DID change is discoverability: the Admin
 * panel's sidebar link is OWNER-or-ADMIN only, so a plain member no longer has a route to this
 * screen even though they may still load it. That is a product decision to confirm, not one to
 * absorb silently.
 */
export function ObservabilityPanel() {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="oa-toolbar flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-2">
        <p className="oa-meta min-w-0 flex-1 basis-52 truncate">
          Agent, tool and service health from the metrics tables.
        </p>
      </div>

      {/*
        `p-[var(--oa-gutter)]` matches the other tabs' body padding rather than the page's old
        spacing, so switching to this tab does not shift every left edge by a few pixels — the same
        alignment problem `OrgAdminLayout`'s inner div exists to solve.
      */}
      <div className="min-h-0 flex-1 overflow-y-auto p-[var(--oa-gutter)]">
        <ObservabilityLayout />
      </div>
    </div>
  );
}
